/**
 * CME Info Overlay
 * Displays market status and countdown timer in chart overlay
 */

import React, { useState, useEffect } from 'react';
import { useMarketStore, SessionType } from '../../store/marketStore';
import './CMEInfoOverlay.css';

interface SessionConfig {
  label: string;
  shortLabel: string;
  color: string;
}

const SESSION_CONFIG: Record<SessionType, SessionConfig> = {
  'closed': { label: 'Closed', shortLabel: 'CLOSED', color: '#ff6b6b' },
  'maintenance': { label: 'Maintenance', shortLabel: 'MAINT', color: '#ffa94d' },
  'asian': { label: 'Asian', shortLabel: 'ASIA', color: '#74c0fc' },
  'european': { label: 'European', shortLabel: 'EUR', color: '#63e6be' },
  'us-premarket': { label: 'Pre-Market', shortLabel: 'PRE', color: '#ffd43b' },
  'rth': { label: 'RTH', shortLabel: 'RTH', color: '#69db7c' },
  'us-postmarket': { label: 'Post-Market', shortLabel: 'POST', color: '#da77f2' },
};

const CMEInfoOverlay: React.FC = React.memo(() => {
  const {
    isMarketOpen,
    currentSession,
    isRTH,
    isMaintenance,
    nextEvent,
    upcomingHoliday,
    nextHoliday,
  } = useMarketStore();

  const [timeRemaining, setTimeRemaining] = useState<string>('');

  useEffect(() => {
    if (!nextEvent) {
      setTimeRemaining('');
      return;
    }

    const updateCountdown = () => {
      const now = Date.now();
      const diff = nextEvent.timestamp - now;

      if (diff <= 0) {
        setTimeRemaining('Now');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (hours > 24) {
        const days = Math.floor(hours / 24);
        const remainingHours = hours % 24;
        setTimeRemaining(`${days}d ${remainingHours}h`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [nextEvent]);

  const sessionConfig = SESSION_CONFIG[currentSession] || SESSION_CONFIG['closed'];

  // Determine market status
  const getStatus = () => {
    if (isMaintenance) return { text: 'MAINT', color: '#ffa94d' };
    if (!isMarketOpen) return { text: 'CLOSED', color: '#ff6b6b' };
    if (isRTH) return { text: 'OPEN', color: '#69db7c' };
    return { text: 'ETH', color: '#74c0fc' };
  };

  const status = getStatus();

  const getEventIcon = (eventType: string) => {
    if (eventType.includes('open') || eventType.includes('resume')) return '▲';
    if (eventType.includes('close') || eventType.includes('maintenance')) return '▼';
    return '●';
  };

  return (
    <div className="cme-info-overlay">
      <div className="cme-info-content">
        {/* Market Status */}
        <div className="cme-info-item">
          <span
            className="cme-status-dot"
            style={{ backgroundColor: status.color }}
          />
          <span
            className="cme-info-value status"
            style={{ color: status.color }}
          >
            {status.text}
          </span>
        </div>

        {/* Session */}
        <div className="cme-info-item">
          <span className="cme-info-label">SESSION:</span>
          <span
            className="cme-info-value session"
            style={{ color: sessionConfig.color }}
          >
            {sessionConfig.shortLabel}
          </span>
        </div>

        {/* Countdown */}
        {nextEvent && timeRemaining && (
          <div className="cme-info-item">
            <span
              className="cme-event-icon"
              style={{ color: nextEvent.type.includes('open') ? '#69db7c' : '#ff6b6b' }}
            >
              {getEventIcon(nextEvent.type)}
            </span>
            <span className="cme-info-value countdown">{timeRemaining}</span>
          </div>
        )}

        {/* Today's Holiday (if early close) */}
        {upcomingHoliday && upcomingHoliday.isEarlyClose && (
          <div className="cme-info-item holiday">
            <span className="cme-info-label" style={{ color: '#ffa94d' }}>⚠</span>
            <span className="cme-info-value" style={{ color: '#ffa94d' }}>
              {upcomingHoliday.name} - Close {upcomingHoliday.closeTime ? (() => {
                // Convert CT close time to PST (CT - 2h)
                const parts = upcomingHoliday.closeTime!.split(':').map(Number);
                const pstHour = parts[0] - 2;
                const h = pstHour < 0 ? pstHour + 24 : pstHour;
                return `${h}:${parts[1].toString().padStart(2, '0')} PST`;
              })() : 'TBD'}
            </span>
          </div>
        )}

        {/* Next Holiday - show early close time if available */}
        {nextHoliday && (
          <div className="cme-info-item holiday-next">
            <span className="cme-info-label" style={{ color: '#ffa94d' }}>⚠</span>
            <span className="cme-info-value" style={{ color: '#ffa94d' }}>
              {nextHoliday.name}{nextHoliday.isEarlyClose && nextHoliday.closeTime ? (() => {
                const parts = nextHoliday.closeTime!.split(':').map(Number);
                const pstH = parts[0] - 2;
                const h = pstH < 0 ? pstH + 24 : pstH;
                const ampm = h >= 12 ? 'p' : 'a';
                const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
                return ` closes ${h12}:${parts[1].toString().padStart(2, '0')}${ampm}`;
              })() : ` ${new Date(nextHoliday.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

export default CMEInfoOverlay;
